import React from 'react';
import { useSelector } from 'react-redux';

import {
    Button,
    ButtonsGroup,
    Modal,
    Title,
    View
} from '../../common';
import { ButtonWithTimer } from '../../common/ButtonWithTimer';
import {
    CHILD_HISTORY_KEY,
    STORE_PROFILE_CHILD,
    USER_HISTORY_KEY
} from '../../hooks/usePersonalData';
import { getProviders } from '../../selectors/consents';

/**
 * Check if consent retraction modal should be shown
 *
 * Modal should only be shown if user is NOT checking a consent that is being displayed.
 * We check the CURRENT VALUES from UI state, not the saved values.
 *
 * For main profile:
 * - Show if USER_HISTORY_KEY is displayed (shouldShow=true) but current value is NOT true
 * - OR if any clinic in USER_CLINIC_HISTORY_KEY is displayed (shouldShow=true) but current value is NOT true
 *
 * For child profile:
 * - Show if STORE_PROFILE_CHILD is displayed (shouldShow=true) but current value is NOT true
 * - OR if CHILD_HISTORY_KEY is displayed (shouldShow=true) but current value is NOT true
 * - OR if any clinic in CHILD_CLINIC_HISTORY_KEY is displayed (shouldShow=true) but current value is NOT true
 *
 * @param {Object} userConsents - User consents object (saved values - for determining shouldShow)
 * @param {string} historyKey - Key for history consent (USER_HISTORY_KEY or CHILD_HISTORY_KEY)
 * @param {string} clinicHistoryKey - Key for clinic history (USER_CLINIC_HISTORY_KEY or CHILD_CLINIC_HISTORY_KEY)
 * @param {Array} clinicsConsents - Array of clinic consents with shouldShow property
 * @param {Object} clinicConsentsState - CURRENT state of clinic consents (from UI)
 * @param {boolean} isChildProfile - Whether this is a child profile
 * @param {string} storeProfileChildKey - Key for STORE_PROFILE_CHILD consent (only for child profile)
 * @param {boolean} historyConsentsChecked - CURRENT value of history consent from UI (for main profile)
 * @param {boolean} storeProfileChildChecked - CURRENT value of STORE_PROFILE_CHILD from UI (for child profile)
 * @returns {boolean} True if modal should be shown
 */
export const shouldShowConsentRetractionModal = (
    userConsents,
    historyKey,
    clinicHistoryKey,
    clinicsConsents = [],
    clinicConsentsState = {},
    isChildProfile = false,
    storeProfileChildKey = null,
    historyConsentsChecked = false,
    storeProfileChildChecked = false,
    hasVisits,
) => {
    // For child profile
    if (isChildProfile) {
        // Check STORE_PROFILE_CHILD - show modal only if displayed but NOT checked
        const storeProfileChildValue = userConsents?.[storeProfileChildKey];
        const storeProfileChildShouldShow = storeProfileChildValue !== true && storeProfileChildValue !== false;

        if (storeProfileChildShouldShow && !storeProfileChildChecked) {
            return true;
        }

        // Check CHILD_HISTORY_KEY - show if displayed but NOT checked
        const historyConsentValue = userConsents?.[historyKey];
        const historyConsentShouldShow = historyConsentValue !== true && historyConsentValue !== false;

        if (historyConsentShouldShow && !historyConsentsChecked && (historyConsentValue !== null || hasVisits)) {
            return true;
        }

        // Check clinic consents in CHILD_CLINIC_HISTORY_KEY group
        if (clinicsConsents && clinicsConsents.length > 0) {
            for (const clinic of clinicsConsents) {
                if (clinic.shouldShow) {
                    const currentClinicState = clinicConsentsState[clinic.providerCode];
                    // Show modal only if clinic is displayed but NOT checked by user
                    if (!currentClinicState) {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    // For main profile
    // Check if USER_HISTORY_KEY is displayed but NOT checked
    const historyConsentValue = userConsents?.[historyKey];
    const historyConsentShouldShow = historyConsentValue !== true && historyConsentValue !== false;

    if (historyConsentShouldShow && !historyConsentsChecked && (historyConsentValue !== null || hasVisits)) {
        return true;
    }

    // Check clinic consents in USER_CLINIC_HISTORY_KEY group - only those with shouldShow=true
    if (clinicsConsents && clinicsConsents.length > 0) {
        for (const clinic of clinicsConsents) {
            if (clinic.shouldShow) {
                const currentClinicState = clinicConsentsState[clinic.providerCode];
                // Show modal only if clinic is displayed but NOT checked by user
                if (!currentClinicState) {
                    return true;
                }
            }
        }
    }

    return false;
};

/**
 * Check for main profile which blocks should be shown based on retracted consents
 * @param {Object} params
 * @param {Object} params.mainConsents - Main consents object (saved values)
 * @param {boolean} params.userHistoryChecked - Current USER_HISTORY_KEY value from UI
 * @param {boolean} params.feedbackAuthorNameChecked - Current FEEDBACK_AUTHOR_NAME value from UI
 * @param {Object} params.historyClinicsState - Current history clinics state from UI
 * @param {Object} params.profileConsents - Profile consents for accessing clinic values
 * @param {string} params.userClinicHistoryKey - USER_CLINIC_HISTORY_KEY constant
 * @returns {Object} { shouldShow: boolean, showMedicalDataHistoryBlock: boolean, showClinicBlock: boolean, uncheckedClinics: Array }
 */
const checkMainProfileRetraction = ({
    mainConsents,
    userHistoryChecked,
    feedbackAuthorNameChecked,
    historyClinicsState,
    profileConsents,
    userClinicHistoryKey,
}) => {
    let showMedicalDataHistoryBlock = false;
    let showClinicBlock = false;
    const uncheckedClinics = [];

    // Check if USER_HISTORY_KEY was retracted (was true, became false)
    if (mainConsents?.[USER_HISTORY_KEY] === true && !userHistoryChecked) {
        showMedicalDataHistoryBlock = true;
    }

    // Check clinic consents - if any was retracted (was true, became false)
    if (profileConsents) {
        Object.keys(profileConsents)
            .forEach((providerCode) => {
                if (providerCode !== 'main') {
                    const clinicConsent = profileConsents[providerCode]?.[userClinicHistoryKey];
                    const currentValue = historyClinicsState[providerCode];

                    // If consent was true and became false
                    if (clinicConsent === true && !currentValue) {
                        showClinicBlock = true;
                        uncheckedClinics.push({
                            name: providerCode, // or get clinic name from somewhere
                            providerCode: providerCode,
                        });
                    }
                }
            });
    }

    const shouldShow = showMedicalDataHistoryBlock || showClinicBlock;

    return {
        shouldShow,
        showMedicalDataHistoryBlock,
        showClinicBlock,
        showChildStorageBlock: false,
        uncheckedClinics,
    };
};

/**
 * Check for child profile which blocks should be shown based on retracted consents
 * @param {Object} params
 * @param {Object} params.mainConsents - Main consents object (saved values)
 * @param {boolean} params.storeProfileChildChecked - Current STORE_PROFILE_CHILD value from UI
 * @param {boolean} params.childHistoryChecked - Current CHILD_HISTORY_KEY value from UI
 * @param {Object} params.historyClinicsState - Current history clinics state from UI
 * @param {Object} params.profileConsents - Profile consents for accessing clinic values
 * @param {string} params.childClinicHistoryKey - CHILD_CLINIC_HISTORY_KEY constant
 * @returns {Object} { shouldShow: boolean, showMedicalDataHistoryBlock: boolean, showClinicBlock: boolean, showChildStorageBlock: boolean, uncheckedClinics: Array }
 */
const checkChildProfileRetraction = ({
    mainConsents,
    storeProfileChildChecked,
    childHistoryChecked,
    historyClinicsState,
    profileConsents,
    childClinicHistoryKey,
}) => {
    let showMedicalDataHistoryBlock = false;
    let showClinicBlock = false;
    let showChildStorageBlock = false;
    const uncheckedClinics = [];

    // Check if STORE_PROFILE_CHILD was retracted (was true, became false)
    if (mainConsents?.[STORE_PROFILE_CHILD] === true && !storeProfileChildChecked) {
        showChildStorageBlock = true;
        return {
            shouldShow: true,
            showMedicalDataHistoryBlock,
            showClinicBlock,
            showChildStorageBlock,
            uncheckedClinics,
        };
    }

    // Check if CHILD_HISTORY_KEY was retracted (was true, became false)
    if (mainConsents?.[CHILD_HISTORY_KEY] === true && !childHistoryChecked) {
        showMedicalDataHistoryBlock = true;
    }

    // Check clinic consents - if any was retracted (was true, became false)
    if (profileConsents) {
        Object.keys(profileConsents)
            .forEach((providerCode) => {
                if (providerCode !== 'main') {
                    const clinicConsent = profileConsents[providerCode]?.[childClinicHistoryKey];
                    const currentValue = historyClinicsState[providerCode];

                    // If consent was true and became false
                    if (clinicConsent === true && !currentValue) {
                        showClinicBlock = true;
                        uncheckedClinics.push({
                            name: providerCode, // or get clinic name from somewhere
                            providerCode: providerCode,
                        });
                    }
                }
            });
    }

    const shouldShow = showMedicalDataHistoryBlock || showClinicBlock || showChildStorageBlock;

    return {
        shouldShow,
        showMedicalDataHistoryBlock,
        showClinicBlock,
        showChildStorageBlock,
        uncheckedClinics,
    };
};

/**
 * Check if consent retraction modal should be shown for DocumentsConsents screen
 * This function checks which consents were changed from true to false and determines
 * which modal blocks should be displayed
 *
 * @param {Object} params
 * @param {boolean} params.isChildProfile - Whether this is a child profile
 * @param {Object} params.profileConsents - Profile consents object (all consents for selected profile)
 * @param {Object} params.mainConsents - Main consents object
 * @param {boolean} params.userHistoryChecked - Current USER_HISTORY_KEY value (for main profile)
 * @param {boolean} params.feedbackAuthorNameChecked - Current FEEDBACK_AUTHOR_NAME value (for main profile)
 * @param {boolean} params.storeProfileChildChecked - Current STORE_PROFILE_CHILD value (for child profile)
 * @param {boolean} params.childHistoryChecked - Current CHILD_HISTORY_KEY value (for child profile)
 * @param {Object} params.historyClinicsState - Current history clinics state from UI
 * @param {string} params.userClinicHistoryKey - USER_CLINIC_HISTORY_KEY constant
 * @param {string} params.childClinicHistoryKey - CHILD_CLINIC_HISTORY_KEY constant
 * @returns {Object} { shouldShow: boolean, showMedicalDataHistoryBlock: boolean, showClinicBlock: boolean, showChildStorageBlock: boolean, uncheckedClinics: Array }
 */
export const shouldShowDocumentsConsentsRetractionModal = ({
    isChildProfile,
    profileConsents,
    mainConsents,
    userHistoryChecked,
    feedbackAuthorNameChecked,
    storeProfileChildChecked,
    childHistoryChecked,
    historyClinicsState,
    userClinicHistoryKey,
    childClinicHistoryKey,
}) => {
    if (isChildProfile) {
        return checkChildProfileRetraction({
            mainConsents,
            storeProfileChildChecked,
            childHistoryChecked,
            historyClinicsState,
            profileConsents,
            childClinicHistoryKey,
        });
    } else {
        return checkMainProfileRetraction({
            mainConsents,
            userHistoryChecked,
            feedbackAuthorNameChecked,
            historyClinicsState,
            profileConsents,
            userClinicHistoryKey,
        });
    }
};

/**
 * ConsentRetractionModal component
 *
 * @param {Object} props
 * @param {Object} props.profile - Profile data {name, dateOfBirth}
 * @param {Array} props.uncheckedClinics - List of unchecked clinics with names
 * @param {boolean} props.showMedicalDataHistoryBlock - Show medical data history block
 * @param {boolean} props.showClinicBlock - Show clinic retraction block
 * @param {boolean} props.showChildStorageBlock - Show child storage retraction block (only for children)
 * @param {function} props.onConfirm - Callback when user confirms deletion
 * @param {function} props.onCancel - Callback when user cancels
 */
export const ConsentRetractionModal = ({
    profile,
    uncheckedClinics = [],
    showMedicalDataHistoryBlock = false,
    showClinicBlock = false,
    showChildStorageBlock = false,
    onConfirm,
    onCancel,
}) => {
    // Determine if we should show profile information
    const showButtonWithTimer = showMedicalDataHistoryBlock || showChildStorageBlock;
    const clinics = useSelector(getProviders);

    // Format profile name and birthday
    const formatProfileInfo = () => {
        if (!profile) {
            return '';
        }
        const {
            name = '',
            birthday = '',
        } = profile;
        return birthday ? `${name}, ${birthday}` : name;
    };

    return (
        <Modal hideClose>
            <Title
                style={{
                    alignSelf: 'center',
                    textAlign: 'center',
                }}
                bold
                id="titles.attention"
            />
            <View style={{
                marginTop: 12,
                paddingLeft: 12,
            }}>
                {/* Medical Data History Block - for main profile */}
                {showMedicalDataHistoryBlock && (
                    <View style={{ marginBottom: 12 }}>
                        <Title numberOfLines={null}>
                            {Object.R('titles.MHConsentsRetractionSubtitle')}
                        </Title>
                        <Title
                            style={{ marginTop: 6 }}
                            bold
                            numberOfLines={null}>
                            {formatProfileInfo()}
                        </Title>
                    </View>
                )}

                {/* Clinic Retraction Block - for main profile */}
                {showClinicBlock && (
                    <View style={{ marginBottom: 12 }}>
                        <Title numberOfLines={null}>
                            {Object.R('titles.MHCConsentsRetractionSubtitle')}
                        </Title>
                        {uncheckedClinics && uncheckedClinics.length > 0 && (
                            <View style={{ marginTop: 6 }}>
                                {uncheckedClinics.map((clinic, index) => (
                                    <Title
                                        bold
                                        key={index}
                                        numberOfLines={null}>
                                        {clinics?.[clinic.name]?.name ?? clinic.name}
                                    </Title>
                                ))}
                            </View>
                        )}
                    </View>
                )}

                {/* Child Storage Retraction Block - only for children */}
                {showChildStorageBlock && (
                    <View>
                        <Title numberOfLines={null}>
                            {Object.R('titles.StoreProfileRetractionSubtitle')}
                        </Title>
                        <Title
                            bold
                            numberOfLines={null}>
                            {formatProfileInfo()}
                        </Title>
                    </View>
                )}
            </View>

            {/* Buttons Group */}
            <ButtonsGroup>
                <Button
                    transparent
                    title="button.cancelButton"
                    action={onCancel}
                />
                {showButtonWithTimer ? (
                    // When profile info is shown, use ButtonWithTimer
                    <ButtonWithTimer
                        danger
                        title="buttons.deleteData"
                        action={onConfirm}
                    />
                ) : (
                    // When no profile info, use regular Button with danger style
                    <Button
                        danger
                        title="buttons.confirm"
                        action={onConfirm}
                    />
                )}
            </ButtonsGroup>
        </Modal>
    );
};
